import { Suspense, useState } from "react";
import { History } from "lucide-react";
import { useTranslations } from "use-intl";
import { WorkOrdersFieldsFragment, WorkOrderStatus } from "@/gql/graphql";
import { WorkOrderAdminNotes } from "@/app/ProDrivers/WorkOrders/Components/WorkOrderAdminNotes";
import { WorkOrderSimilarNotes } from "@/app/ProDrivers/WorkOrders/Components/WorkOrderSimilarNotes";
import { cn } from "@/lib/utils";
import { Rome2RioIcon } from "@/app/ProDrivers/WorkOrders/Components/Rome2RioIcon";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

function rome2RioSlug(address: WorkOrdersFieldsFragment["deliveryAddress"]) {
  return [address.formatted_address_line_1, address.formatted_address_line_2]
    .filter(Boolean)
    .join(" ")
    .replace(/,/g, "")
    .replace(/ /g, "-");
}

function rome2RioUrl(workOrder: WorkOrdersFieldsFragment) {
  return `https://www.rome2rio.com/map/${rome2RioSlug(workOrder.deliveryAddress)}/${rome2RioSlug(workOrder.departureAddress)}`;
}

export function WorkOrderNotes({
  workOrder,
}: {
  workOrder: WorkOrdersFieldsFragment;
}) {
  const t = useTranslations("workOrder.details");
  const [showSimilarNotes, setShowSimilarNotes] = useState(
    workOrder.status === WorkOrderStatus.QuoteRequested,
  );

  const similarNotesLabel = showSimilarNotes
    ? t("hideSimilarWorkOrderNotes")
    : t("showSimilarWorkOrderNotes");

  return (
    <div
      className={cn("grid gap-4", showSimilarNotes ? "lg:grid-cols-2" : null)}
    >
      <div className="space-y-2">
        <WorkOrderAdminNotes
          workOrderId={workOrder.id}
          adminNotes={workOrder.admin_notes}
        />
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={similarNotesLabel}
                aria-pressed={showSimilarNotes}
                onClick={() => setShowSimilarNotes((isShown) => !isShown)}
                className={cn(
                  "rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700",
                  showSimilarNotes ? "bg-gray-100 text-gray-700" : null,
                )}
              >
                <History className="size-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent>{similarNotesLabel}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <a
                href={rome2RioUrl(workOrder)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("viewReturnOptions")}
                className="rounded-lg p-1.5 hover:bg-gray-100"
              >
                <Rome2RioIcon className="size-5" />
              </a>
            </TooltipTrigger>
            <TooltipContent>{t("viewReturnOptions")}</TooltipContent>
          </Tooltip>
        </div>
      </div>
      {showSimilarNotes ? (
        <Suspense>
          <WorkOrderSimilarNotes workOrderId={workOrder.id} />
        </Suspense>
      ) : null}
    </div>
  );
}
